<template>
  <div class="tracking-page">

    <!-- Cabecera estándar: título + buscador compacto -->
    <div class="page-header">
      <div>
        <h1 class="page-header-title"><i :class="icono"></i> {{ titulo }}</h1>
        <p class="page-header-sub">{{ subtitulo }}</p>
      </div>
      <form class="tk-buscador" @submit.prevent="buscar()">
        <i class="bi bi-search tk-buscador-icono"></i>
        <input
          type="text"
          v-model="numeroRastreo"
          class="form-control"
          :placeholder="`N° de rastreo, ej. ${ejemplo}`"
          aria-label="Número de rastreo"
        />
        <button type="submit" class="btn-filter" :disabled="cargando || !numeroRastreo.trim()">
          <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
          Buscar
        </button>
      </form>
    </div>

    <!-- Estados vacíos -->
    <div v-if="cargando && !envio" class="tk-card tk-vacio">
      <span class="spinner-border text-primary"></span>
      <p>Buscando el envío…</p>
    </div>
    <div v-else-if="!buscado" class="tk-card tk-vacio">
      <i class="bi bi-upc-scan"></i>
      <p>Ingresa un número de rastreo para ver el estado del envío.</p>
    </div>
    <div v-else-if="!envio" class="tk-card tk-vacio">
      <i class="bi bi-search"></i>
      <p>No se encontró ningún envío con el número <b>{{ rastreoBuscado }}</b>.</p>
      <small>Revisa que el número esté completo, por ejemplo {{ ejemplo }}.</small>
    </div>

    <!-- En pantallas anchas el historial va en una columna a la derecha; en laptops, debajo -->
    <div v-else class="tk-contenido">
      <!-- Resumen + progreso -->
      <div class="tk-card tk-principal" :class="{ 'tk-cargando': cargando }">
        <div v-if="especial" class="tk-alerta" :class="`tk-alerta-${especial.color}`">
          <i :class="especial.icono"></i>
          {{ especial.texto }}
        </div>

        <div class="tk-resumen-cabecera">
          <div>
            <div class="tk-etiqueta">N° de rastreo</div>
            <div class="tk-codigo">{{ envio.rastreo }}</div>
          </div>
          <span class="tk-estado" :class="claseEstado">
            <span class="tk-estado-punto"></span>{{ estadoActualTitulo }}
          </span>
        </div>

        <dl class="tk-datos">
          <div v-for="dato in datosEnvio" :key="dato.etiqueta" class="tk-dato"
            :class="{ 'tk-dato-ancho': dato.ancho, 'tk-dato-completo': dato.completo }">
            <dt><i :class="dato.icono"></i> {{ dato.etiqueta }}</dt>
            <dd>
              {{ dato.valor }}
              <small v-if="dato.detalle">{{ dato.detalle }}</small>
            </dd>
          </div>
        </dl>

        <!-- Progreso -->
        <div class="tk-progreso-scroll">
          <ol class="tk-progreso" :class="{ 'tk-progreso-cancelado': especial && especial.color === 'roja' }">
            <li v-for="(paso, index) in pasos" :key="index" :class="['tk-paso', claseDePaso(index)]">
              <span class="tk-paso-circulo">
                <i v-if="claseDePaso(index) === 'step-done'" class="bi bi-check-lg"></i>
                <i v-else :class="paso.icono"></i>
              </span>
              <span class="tk-paso-texto">{{ paso.titulo }}</span>
            </li>
          </ol>
        </div>
      </div>

      <!-- Historial (más reciente primero) -->
      <div class="tk-card tk-lateral">
        <h2 class="tk-titulo"><i class="bi bi-clock-history"></i> Historial y evidencias</h2>

        <p v-if="!agrupadoPorEstado.length" class="tk-sin-historial">Aún no hay movimientos registrados para este envío.</p>

        <ol v-else class="tk-historial">
          <li v-for="(grupo, i) in agrupadoPorEstado" :key="grupo.idEstado" class="tk-evento" :class="{ 'tk-evento-ultimo': i === 0 }">
            <div class="tk-evento-cabecera">
              <strong>{{ grupo.estado }}</strong>
              <span class="tk-evento-fecha">{{ formatoFechaHora(grupo.created_at) }}</span>
            </div>

            <div v-if="archivosDe(grupo).length" class="tk-archivos">
              <a v-for="item in archivosDe(grupo)" :key="item.id" :href="item.url" target="_blank" rel="noopener noreferrer"
                class="tk-archivo" :title="item.nombre">
                <!-- Si la imagen no carga (archivo movido o borrado) se muestra el ícono, no un cuadro vacío -->
                <img v-if="esImagen(item.extension) && !imagenesFallidas[item.id]" :src="item.url" :alt="item.nombre" loading="lazy"
                  @error="$set(imagenesFallidas, item.id, true)" />
                <span v-else class="tk-archivo-icono"><i :class="iconoArchivo(item.extension)"></i>{{ item.extension }}</span>
                <span class="tk-archivo-tipo">{{ item.tipo || 'Archivo' }}</span>
              </a>
            </div>

            <div v-for="item in obtenerComentarios(grupo)" :key="'c-' + item.id" class="tk-comentario">
              <i class="bi bi-chat-left-text"></i>
              <div>
                <p>{{ item.comentario }}</p>
                <small>{{ formatoFechaHora(item.created_at) }}</small>
              </div>
            </div>

            <p v-if="!archivosDe(grupo).length && !obtenerComentarios(grupo).length" class="tk-sin-historial">
              Sin archivos ni comentarios en este estado.
            </p>
          </li>
        </ol>
      </div>
    </div>

  </div>
</template>

<script>
// Rastreo de un envío (local o provincia): misma pantalla, cada página define sus estados y datos.
import axios from 'axios'

// Helpers de formato que reciben las páginas para armar el resumen
export const formato = {
  // "2026-10-23" → "23/10/2026" sin pasar por Date (evita el desfase de zona horaria)
  fecha(fecha) {
    if (!fecha) return ''
    const [a, m, d] = String(fecha).slice(0, 10).split('-')
    return d && m && a ? `${d}/${m}/${a}` : String(fecha)
  },
  // "16:00:00" → "16:00"; textos como "3:00 PM" se dejan tal cual
  hora(hora) {
    if (!hora) return ''
    return /^\d{2}:\d{2}/.test(hora) ? String(hora).slice(0, 5) : String(hora)
  },
  unir(...valores) {
    return valores.filter(v => v !== null && v !== undefined && String(v).trim() !== '').join(' · ')
  },
  // "EMPRESA - SUCURSAL": si la sucursal se llama igual que la empresa, se muestra una sola vez
  empresaSucursal(texto) {
    if (!texto) return texto
    return [...new Set(String(texto).split(' - ').map(p => p.trim()).filter(Boolean))].join(' · ')
  },
}

export default {
  name: 'RastreoEnvio',
  props: {
    titulo: { type: String, required: true },
    subtitulo: { type: String, required: true },
    icono: { type: String, default: 'bi bi-geo-alt' },
    ejemplo: { type: String, required: true },          // número de ejemplo para ayudar al usuario
    endpoint: { type: String, required: true },         // p. ej. 'tracking-local' → GET tracking-local/{numero}
    campoEstado: { type: String, required: true },      // campo del envío con el id del estado
    campoEstadoArchivo: { type: String, required: true }, // campo del archivo con el id del estado
    // Pasos del progreso: { ids: [..], titulo, icono }. Un paso puede agrupar varios estados.
    pasos: { type: Array, required: true },
    // Desde qué paso (índice) el envío se considera entregado (insignia en verde)
    pasoEntregado: { type: Number, required: true },
    // Estados fuera del flujo: { [id]: { texto, color: 'roja' | 'naranja', icono } }
    especiales: { type: Object, default: () => ({}) },
    // (envio, formato) => [{ etiqueta, icono, valor, detalle?, ancho?, completo? }]
    datos: { type: Function, required: true },
  },
  data() {
    return {
      numeroRastreo: '',
      rastreoBuscado: '',
      envio: null,
      cargando: false,
      buscado: false,
      imagenesFallidas: {},
    }
  },
  mounted() {
    this.cargarDesdeUrl()
  },
  watch: {
    // Entrar a otro rastreo desde un enlace (misma página, distinto ?numeroRastreo)
    '$route.query.numeroRastreo'() {
      this.cargarDesdeUrl()
    },
  },
  computed: {
    // El API manda el estado como texto: se compara siempre como número
    estadoActual() {
      return Number(this.envio?.[this.campoEstado]) || 0
    },
    especial() {
      return this.especiales[this.estadoActual] || null
    },
    // Posición del estado actual en el progreso (-1 si está fuera del flujo)
    indiceActual() {
      return this.pasos.findIndex(p => p.ids.map(Number).includes(this.estadoActual))
    },
    estadoActualTitulo() {
      // El nombre lo da el sistema (cubre estados especiales y cualquier estado nuevo)
      return this.envio?.estado_envio || this.pasos[this.indiceActual]?.titulo || 'Sin estado'
    },
    claseEstado() {
      if (this.especial) return this.especial.color === 'roja' ? 'tk-estado-rojo' : 'tk-estado-naranja'
      if (this.indiceActual >= this.pasoEntregado) return 'tk-estado-verde'
      return 'tk-estado-azul'
    },
    // Solo se muestran los datos que tienen valor
    datosEnvio() {
      if (!this.envio) return []
      return this.datos(this.envio, formato)
        .filter(d => d.valor !== null && d.valor !== undefined && String(d.valor).trim() !== '')
    },
    agrupadoPorEstado() {
      const agrupado = {}
      ;(this.envio?.archivos || []).forEach(item => {
        const idEstado = item[this.campoEstadoArchivo]
        const extension = item.nombre?.split('.').pop()?.toLowerCase() || ''
        if (!agrupado[idEstado]) {
          agrupado[idEstado] = { estado: item.estado, idEstado, created_at: item.created_at, imagenes: [] }
        }
        agrupado[idEstado].imagenes.push({ ...item, extension })
        if (new Date(item.created_at) > new Date(agrupado[idEstado].created_at)) {
          agrupado[idEstado].created_at = item.created_at
        }
      })
      return Object.values(agrupado).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },
  },
  methods: {
    cargarDesdeUrl() {
      const param = (this.$route.query.numeroRastreo || '').toString().trim()
      if (param) {
        this.numeroRastreo = param
        this.getDatos()
      }
    },
    buscar() {
      const numero = this.numeroRastreo.trim()
      if (!numero) return
      // La URL refleja la búsqueda (se puede recargar o compartir el enlace).
      // Si cambia, el watcher de la ruta hace la carga; si es el mismo número, se vuelve a consultar aquí.
      if (this.$route.query.numeroRastreo !== numero) {
        this.$router.replace({ query: { ...this.$route.query, numeroRastreo: numero } }).catch(() => {})
      } else {
        this.getDatos()
      }
    },
    claseDePaso(index) {
      if (index === this.indiceActual) return 'step-active'
      if (this.indiceActual > -1 && index < this.indiceActual) return 'step-done'
      return 'step-pending'
    },
    archivosDe(grupo) {
      return grupo.imagenes.filter(i => i.nombre)
    },
    obtenerComentarios(grupo) {
      return grupo.imagenes.filter(item => (item.comentario || '').toString().trim().length > 0)
    },
    async getDatos() {
      const numero = this.numeroRastreo.trim()
      if (!numero) return
      this.cargando = true
      try {
        const { data } = await axios.get(`${this.endpoint}/${encodeURIComponent(numero)}`)
        // Si no existe, el API responde vacío: no se debe seguir mostrando el envío anterior
        this.envio = data && data.rastreo ? data : null
      } catch (error) {
        console.error('Error al obtener datos de tracking:', error)
        this.envio = null
      } finally {
        this.rastreoBuscado = numero
        this.buscado = true
        this.cargando = false
      }
    },
    formatoFechaHora(fecha) {
      if (!fecha) return ''
      return new Date(fecha).toLocaleString('es-PE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    },
    iconoArchivo(extension) {
      if (this.esImagen(extension)) return 'bi bi-image'
      if (extension === 'pdf') return 'bi bi-file-earmark-pdf'
      if (['xls', 'xlsx', 'csv'].includes(extension)) return 'bi bi-file-earmark-excel'
      if (['doc', 'docx'].includes(extension)) return 'bi bi-file-earmark-word'
      if (['zip', 'rar'].includes(extension)) return 'bi bi-file-earmark-zip'
      if (['mp4', 'mov', 'avi', 'webm'].includes(extension)) return 'bi bi-file-earmark-play'
      return 'bi bi-file-earmark'
    },
    esImagen(extension) {
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)
    },
  },
}
</script>

<style scoped>
/* ── Distribución: una columna en laptops; resumen + historial lado a lado en pantallas anchas ── */
.tk-contenido { display: block; }
@media (min-width: 1500px) {
  .tk-contenido {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(380px, 440px);
    gap: 16px;
    align-items: start;
  }
  .tk-contenido > .tk-card { margin-bottom: 0; }
  /* El historial acompaña al bajar y, si es largo, se desplaza dentro de su columna */
  .tk-lateral {
    position: sticky;
    top: 16px;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
  }
}

/* ── Buscador en la cabecera ── */
.tk-buscador {
  position: relative;
  display: flex;
  gap: 8px;
  width: 420px;
  max-width: 100%;
}
.tk-buscador-icono {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #8592a3;
  font-size: 0.85rem;
  pointer-events: none;
}
.tk-buscador .form-control { padding-left: 34px !important; height: 36px; }
.tk-buscador .btn-filter { height: 36px; white-space: nowrap; }
.tk-buscador .btn-filter:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Tarjetas ── */
.tk-card {
  background: #fff;
  border: 1px solid #eceef3;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 16px;
  transition: opacity 0.15s;
}
.tk-cargando { opacity: 0.6; }

.tk-vacio {
  text-align: center;
  padding: 48px 24px;
  color: #6b7a8c;
}
.tk-vacio > i { font-size: 2.2rem; color: #c5cae9; }
.tk-vacio p { margin: 12px 0 4px; font-size: 0.9rem; }
.tk-vacio small { color: #8592a3; }

.tk-alerta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.88rem;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.tk-alerta-roja    { background: #ffebee; color: #c62828; }
.tk-alerta-naranja { background: #fff3e0; color: #e65100; }

/* ── Resumen ── */
.tk-resumen-cabecera {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f1f5;
}
.tk-etiqueta {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #8592a3;
}
.tk-codigo {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2b2f3a;
  letter-spacing: 0.3px;
}
.tk-estado {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}
.tk-estado-punto { width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
.tk-estado-azul    { background: #e8eaf6; color: #3949ab; }
.tk-estado-verde   { background: #e8f5e9; color: #2e7d32; }
.tk-estado-naranja { background: #fff3e0; color: #e65100; }
.tk-estado-rojo    { background: #ffebee; color: #c62828; }

.tk-datos {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 24px;
  margin: 16px 0 0;
}
.tk-dato-ancho { grid-column: span 2; }
.tk-dato-completo { grid-column: 1 / -1; }
.tk-dato-completo dd { white-space: pre-line; }
.tk-dato dt {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8592a3;
  margin-bottom: 3px;
}
.tk-dato dt i { margin-right: 3px; }
.tk-dato dd {
  margin: 0;
  font-size: 0.84rem;
  color: #2b2f3a;
  line-height: 1.35;
  overflow-wrap: anywhere;
}
.tk-dato dd small { display: block; color: #6b7a8c; font-size: 0.76rem; }

/* ── Progreso ── */
.tk-progreso-scroll { overflow-x: auto; margin-top: 20px; padding-top: 18px; border-top: 1px solid #f0f1f5; }
.tk-progreso {
  display: flex;
  min-width: 760px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.tk-paso {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
/* línea que une cada paso con el anterior (verde si ya se llegó a este paso) */
.tk-paso + .tk-paso::before {
  content: '';
  position: absolute;
  top: 17px;
  right: 50%;
  width: 100%;
  height: 3px;
  background: #e6e8f0;
  z-index: 0;
}
.tk-paso.step-done::before,
.tk-paso.step-active::before { background: #66bb6a; }
.tk-progreso-cancelado .tk-paso::before { background: #ffcdd2 !important; }

.tk-paso-circulo {
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  background: #fff;
  border: 2px solid #e0e3eb;
  color: #b0b8c4;
}
.step-done .tk-paso-circulo   { background: #43a047; border-color: #43a047; color: #fff; }
.step-active .tk-paso-circulo { background: #3f51b5; border-color: #3f51b5; color: #fff; box-shadow: 0 0 0 4px rgba(63,81,181,0.18); }
.tk-paso-texto {
  margin-top: 6px;
  max-width: 92px;
  font-size: 0.68rem;
  line-height: 1.25;
  color: #8592a3;
}
.step-done .tk-paso-texto   { color: #2e7d32; }
.step-active .tk-paso-texto { color: #3f51b5; font-weight: 700; }

/* ── Historial ── */
.tk-titulo {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2b2f3a;
  margin: 0 0 16px;
}
.tk-titulo i { color: #3f51b5; margin-right: 4px; }
.tk-sin-historial { font-size: 0.82rem; color: #8592a3; margin: 0; }

.tk-historial { list-style: none; margin: 0; padding: 0; }
.tk-evento {
  position: relative;
  padding: 0 0 20px 26px;
  border-left: 2px solid #e6e8f0;
  margin-left: 6px;
}
.tk-evento:last-child { padding-bottom: 0; border-left-color: transparent; }
.tk-evento::before {
  content: '';
  position: absolute;
  left: -7px;
  top: 3px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #9fa8da;
}
.tk-evento-ultimo::before { background: #3f51b5; border-color: #3f51b5; box-shadow: 0 0 0 3px rgba(63,81,181,0.18); }
.tk-evento-cabecera {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.tk-evento-cabecera strong { font-size: 0.88rem; color: #2b2f3a; }
.tk-evento-fecha { font-size: 0.76rem; color: #8592a3; white-space: nowrap; }

.tk-archivos { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 10px; }
.tk-archivo {
  width: 112px;
  text-decoration: none;
  color: inherit;
}
.tk-archivo img,
.tk-archivo-icono {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 84px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e6e8f0;
  background: #f7f8fc;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.tk-archivo-icono { flex-direction: column; gap: 2px; font-size: 0.65rem; text-transform: uppercase; color: #6b7a8c; }
.tk-archivo-icono i { font-size: 1.6rem; color: #3f51b5; }
.tk-archivo:hover img,
.tk-archivo:hover .tk-archivo-icono { border-color: #9fa8da; box-shadow: 0 2px 8px rgba(63,81,181,0.15); }
.tk-archivo-tipo {
  display: block;
  margin-top: 4px;
  font-size: 0.7rem;
  color: #566a7f;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tk-comentario {
  display: flex;
  gap: 10px;
  background: #f7f8fc;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
}
.tk-comentario > i { color: #7986cb; margin-top: 2px; }
.tk-comentario p { margin: 0; font-size: 0.84rem; color: #2b2f3a; line-height: 1.4; white-space: pre-line; }
.tk-comentario small { color: #8592a3; font-size: 0.72rem; }

@media (max-width: 992px) {
  .tk-datos { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 576px) {
  .tk-buscador { width: 100%; }
  .tk-datos { grid-template-columns: 1fr; }
  .tk-dato-ancho,
  .tk-dato-completo { grid-column: auto; }
  .tk-card { padding: 16px; }
}
</style>
